import React, { PropsWithChildren, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import Image from 'next/image'
import Link from 'next/link'

import {
  PortableText,
  PortableTextListComponent,
  PortableTextListItemComponent,
} from '@portabletext/react'

import { blockSerializers } from './index'
import { SocialShareButtons } from '../'
import { CompareCredit } from '../../../types/compare-credit'
import {
  getCurrentMonth,
  getCurrentYear,
  nextImageHelper,
  supify,
} from '../../utils'
import { useStatsigClient } from '@statsig/react-bindings'
import { useShouldShowHighlight } from 'utils/experimentation/useShouldShowHighlight'

const List: React.FC<PropsWithChildren> = ({ children }) => (
  <ul className="c-list-disc / mb-2 pl-5">{children}</ul>
)

const ListItem: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <li className={`c-list-disc__item`}>
      {children}{' '}
      <style jsx>
        {`
          .c-list-disc__item {
            margin-bottom: 0.25em;
          }
          .c-list-disc__item:last-child {
            margin-bottom: 0;
          }
        `}
      </style>
    </li>
  )
}

export const TipHeader = (data: {
  cardOrderIds: Record<string, string>
  referencedCards: null | Record<string, CompareCredit.Entities>
  categoryId: string
  externalId: string
}) => {
  const TipHeaderRender = (props: {
    value: {
      author: CompareCredit.TipV2['author']
      reviewer?: CompareCredit.TipV2['author'][0]
      updatedDate: Date
      author_bar: {
        authorPicture: boolean
        hideAuthorBio: boolean
        dateVariation: boolean
        timeToRead: string
        reviewedBy: boolean
        showSocialIcons: boolean
      }
      borderBottom: boolean
      headline: string
      showAuthor: true
      subtitleEditorToggle: boolean
      subtitle: string
      subtitleAdvertorial: any
      underline: boolean
      hideUnderlineOnMobile: boolean
      location: string
      displayEditorialNote: boolean
    }
  }) => {
    const { getDynamicConfig } = useStatsigClient()

    const {
      author,
      reviewer,
      updatedDate,
      author_bar,
      borderBottom,
      headline,
      showAuthor,
      subtitleEditorToggle,
      subtitle,
      subtitleAdvertorial,
      underline,
      hideUnderlineOnMobile,
      location,
    } = props.value

    const showReviewedBy =
      author_bar.reviewedBy &&
      reviewer &&
      reviewer.slug &&
      reviewer.authorBioToggle

    const { cardOrderIds, referencedCards, categoryId, externalId } = data

    const lastUpdatedDate = useMemo(() => {
      let lastUpdatedDate = updatedDate
      const cards = Object.values(referencedCards || {})
      if (cards && cards.length) {
        cards.forEach((card) => {
          if (
            card._type === 'card' &&
            card._updatedAt &&
            new Date(card._updatedAt) > lastUpdatedDate
          ) {
            lastUpdatedDate = new Date(card._updatedAt)
          }
        })
      }
      return lastUpdatedDate
    }, [updatedDate, referencedCards])

    const socialIconsHide = true

    const tipsUtmTitlesConfig = getDynamicConfig('tips_utm_titles')
    const customPageTitle = tipsUtmTitlesConfig.get(
      'page_title',
      null,
    ) as string

    // 2025.04: EXP-369 Disco Cach Back Headline Highlight (PMAX|Desktop)
    const shouldShowHighlight = useShouldShowHighlight()

    const shouldShowAuthor = useMemo(
      () => Boolean(!isMobile && showAuthor && author && author.length > 0),
      [isMobile, showAuthor, author?.length],
    )

    const showUnderlineDesktop = useMemo(
      () => underline && !isMobile,
      [underline],
    )
    const showUnderlineMobile = useMemo(
      () => underline && isMobile && !hideUnderlineOnMobile,
      [underline, hideUnderlineOnMobile],
    )

    return (
      <div className="c-header__container / flex flex-wrap / mx-auto">
        <div className="c-header__inner / mx-auto w-full max-w-6xl">
          <header className="c-header w-full pt-3">
            <div className="c-header__content / relative / md:flex md:flex-wrap md:justify-start max-w-6xl / mx-auto">
              <div
                className={`c-header__content-section / w-full / ${
                  borderBottom
                    ? 'mb-3 xs:mb-1 lg:mb-4 / pb-2 / border-b border-gray-400'
                    : ''
                }`}
              >
                {shouldShowHighlight ? (
                  <h1
                    className={`c-header__h1 / u-sup / mb-3 / font-serif  text-primary font-bold leading-tight
                    text-[36px] xs:text-[42px]`}
                  >
                    Earn Up to{' '}
                    <span className="block sm:inline-block">
                      <span className="c-header__h1-highlight / relative / inline-block">
                        5% Cash Back
                        <span
                          className="c-header__h1-highlight-stroke / absolute -top-[18%] xs:-top-[9%] / -left-[2%] xs:left-[6%] /
                      scale-x-[110%] xs:scale-x-[132%] sm:scale-x-[126%] / scale-y-[95%] sm:scale-y-[116%] / opacity-40"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="232"
                            height="63"
                            viewBox="0 0 232 63"
                            fill="none"
                          >
                            <path
                              className="c-header__h1-highlight-path"
                              d="M224 31.5001C224 39.2388 213.246 46.6882 193.917 52.339C174.587 57.9898 148.128 61.4193 119.899 61.9329C91.6708 62.4464 63.7841 60.0055 41.8893 55.1047C19.9944 50.2038 5.72865 43.2096 1.9823 35.539C-1.76406 27.8684 5.28913 20.095 21.7129 13.7939C38.1366 7.49269 62.7027 3.13487 90.434 1.60334C118.165 0.0718074 146.988 1.48108 171.063 5.54564C195.137 9.6102 220.5 18 231 31.5001"
                              stroke="#21A3FC"
                              strokeWidth="3"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                      </span>
                      &nbsp;
                      <span className="relative / inline-block">Now</span>
                    </span>
                  </h1>
                ) : (
                  <h1
                    className={`c-header__h1 c-header__h1--base / u-sup / mb-3 / font-serif text-3xl xxs:text-4xl text-primary font-bold leading-tight md:text-fs42 lg:text-fs38`}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: supify(customPageTitle ?? headline),
                      }}
                    />
                  </h1>
                )}
                <h1
                  className={`c-header__h1 c-header__h1--com2-616 hidden / u-sup / mb-3 / font-serif  text-primary font-bold leading-tight
                    text-[28px] xs:text-[42px]`}
                >
                  <span className="block sm:inline-block sm:mr-2">
                    <span className="c-header__h1-highlight c-header__h1-highlight-1 / relative / inline-block ">
                      Zero
                      <span
                        className="c-header__h1-highlight-stroke / ml-0 / opacity-30 / absolute /
                    -top-[.75rem] xs:-top-[.5rem] md:-top-[.25rem] lg:-top-[.35rem] xl:-top-[.2rem] / -left-[5.25rem] xs:-left-[4.75rem] md:-left-[4.45rem] lg:-left-[4.65rem] xl:-left-[4.3rem] /
                    scale-x-[37%] xs:scale-x-[45%] md:scale-x-[55%] lg:scale-x-[48%] xl:scale-x-[55%] / scale-y-[65%] xs:scale-y-[70%] md:scale-y-[75%] lg:scale-y-[80%] xl:scale-y-[90%]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="232"
                          height="63"
                          viewBox="0 0 232 63"
                          fill="none"
                        >
                          <path
                            className="c-header__h1-highlight-path c-header__h1-highlight-path-1"
                            d="M224 31.5001C224 39.2388 213.246 46.6882 193.917 52.339C174.587 57.9898 148.128 61.4193 119.899 61.9329C91.6708 62.4464 63.7841 60.0055 41.8893 55.1047C19.9944 50.2038 5.72865 43.2096 1.9823 35.539C-1.76406 27.8684 5.28913 20.095 21.7129 13.7939C38.1366 7.49269 62.7027 3.13487 90.434 1.60334C118.165 0.0718074 146.988 1.48108 171.063 5.54564C195.137 9.6102 220.5 18 231 31.5001"
                            stroke="#21A3FC"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    </span>
                    &nbsp;
                    <span className="relative / inline-block"> Regrets.</span>
                  </span>
                  {` `}
                  <span className="block sm:inline">
                    <span className="c-header__h1-highlight c-header__h1-highlight-2 / relative / inline-block">
                      Zero
                      <span
                        className="c-header__h1-highlight-stroke / ml-0 / opacity-30 / absolute /
                    -top-[.75rem] xs:-top-[.5rem] md:-top-[.25rem] lg:-top-[.35rem] xl:-top-[.2rem] / -left-[5.25rem] xs:-left-[4.75rem] md:-left-[4.45rem] lg:-left-[4.65rem] xl:-left-[4.3rem] /
                    scale-x-[37%] xs:scale-x-[45%] md:scale-x-[55%] lg:scale-x-[48%] xl:scale-x-[55%] / scale-y-[65%] xs:scale-y-[70%] md:scale-y-[75%] lg:scale-y-[80%] xl:scale-y-[90%]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="232"
                          height="63"
                          viewBox="0 0 232 63"
                          fill="none"
                        >
                          <path
                            className="c-header__h1-highlight-path c-header__h1-highlight-path-2"
                            d="M224 31.5001C224 39.2388 213.246 46.6882 193.917 52.339C174.587 57.9898 148.128 61.4193 119.899 61.9329C91.6708 62.4464 63.7841 60.0055 41.8893 55.1047C19.9944 50.2038 5.72865 43.2096 1.9823 35.539C-1.76406 27.8684 5.28913 20.095 21.7129 13.7939C38.1366 7.49269 62.7027 3.13487 90.434 1.60334C118.165 0.0718074 146.988 1.48108 171.063 5.54564C195.137 9.6102 220.5 18 231 31.5001"
                            stroke="#21A3FC"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    </span>
                    &nbsp;
                    <span className="relative / inline">
                      {' '}
                      Interest for 21&nbsp;Months.
                    </span>
                  </span>
                </h1>
                <h1
                  className={`c-header__h1 c-header__h1--com2-640 hidden / u-sup / mb-3 / font-serif  text-primary font-bold leading-tight
                    text-[28px] xs:text-[42px]`}
                >
                  <span className="block sm:inline-block sm:mr-2">
                    <span className="c-header__h1-highlight c-header__h1-highlight-1 / relative / inline-block ">
                      Zero
                      <span
                        className="c-header__h1-highlight-stroke / ml-0 / opacity-30 / absolute /
                    -top-[.75rem] xs:-top-[.5rem] md:-top-[.25rem] lg:-top-[.35rem] xl:-top-[.2rem] / -left-[5.25rem] xs:-left-[4.75rem] md:-left-[4.45rem] lg:-left-[4.65rem] xl:-left-[4.3rem] /
                    scale-x-[37%] xs:scale-x-[45%] md:scale-x-[55%] lg:scale-x-[48%] xl:scale-x-[55%] / scale-y-[65%] xs:scale-y-[70%] md:scale-y-[75%] lg:scale-y-[80%] xl:scale-y-[90%]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="232"
                          height="63"
                          viewBox="0 0 232 63"
                          fill="none"
                        >
                          <path
                            className="c-header__h1-highlight-path c-header__h1-highlight-path-1"
                            d="M224 31.5001C224 39.2388 213.246 46.6882 193.917 52.339C174.587 57.9898 148.128 61.4193 119.899 61.9329C91.6708 62.4464 63.7841 60.0055 41.8893 55.1047C19.9944 50.2038 5.72865 43.2096 1.9823 35.539C-1.76406 27.8684 5.28913 20.095 21.7129 13.7939C38.1366 7.49269 62.7027 3.13487 90.434 1.60334C118.165 0.0718074 146.988 1.48108 171.063 5.54564C195.137 9.6102 220.5 18 231 31.5001"
                            stroke="#21A3FC"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    </span>
                    &nbsp;
                    <span className="relative / inline-block"> Regrets.</span>
                  </span>
                  {` `}
                  <span className="block sm:inline">
                    <span className="c-header__h1-highlight c-header__h1-highlight-2 / relative / inline-block">
                      Zero
                      <span
                        className="c-header__h1-highlight-stroke / ml-0 / opacity-30 / absolute /
                    -top-[.75rem] xs:-top-[.5rem] md:-top-[.25rem] lg:-top-[.35rem] xl:-top-[.2rem] / -left-[5.25rem] xs:-left-[4.75rem] md:-left-[4.45rem] lg:-left-[4.65rem] xl:-left-[4.3rem] /
                    scale-x-[37%] xs:scale-x-[45%] md:scale-x-[55%] lg:scale-x-[48%] xl:scale-x-[55%] / scale-y-[65%] xs:scale-y-[70%] md:scale-y-[75%] lg:scale-y-[80%] xl:scale-y-[90%]"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="232"
                          height="63"
                          viewBox="0 0 232 63"
                          fill="none"
                        >
                          <path
                            className="c-header__h1-highlight-path c-header__h1-highlight-path-2"
                            d="M224 31.5001C224 39.2388 213.246 46.6882 193.917 52.339C174.587 57.9898 148.128 61.4193 119.899 61.9329C91.6708 62.4464 63.7841 60.0055 41.8893 55.1047C19.9944 50.2038 5.72865 43.2096 1.9823 35.539C-1.76406 27.8684 5.28913 20.095 21.7129 13.7939C38.1366 7.49269 62.7027 3.13487 90.434 1.60334C118.165 0.0718074 146.988 1.48108 171.063 5.54564C195.137 9.6102 220.5 18 231 31.5001"
                            stroke="#21A3FC"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                    </span>
                    &nbsp;
                    <span className="relative / inline">
                      {' '}
                      Interest for up to 21&nbsp;Months.
                    </span>
                  </span>
                </h1>

                {showUnderlineDesktop && (
                  <hr className="c-header__border / border-3 w-24 md:w-32 border-tetriary / mb-5" />
                )}

                {subtitle || subtitleAdvertorial ? (
                  <div className="c-header__subtitle / mb-5 / text-gray-700 sm:text-lg lg:text-xl">
                    {subtitleEditorToggle ? (
                      subtitleAdvertorial ? (
                        <PortableText
                          value={subtitleAdvertorial}
                          components={{
                            ...blockSerializers({
                              cardOrderIds,
                              categoryId,
                              externalId,
                              referencedCards,
                            }),
                            list: List as PortableTextListComponent,
                            listItem: ListItem as PortableTextListItemComponent,
                          }}
                        />
                      ) : (
                        <></>
                      )
                    ) : subtitle ? (
                      <p className="c-header__subtitle-text">{subtitle}</p>
                    ) : (
                      <></>
                    )}
                  </div>
                ) : (
                  <></>
                )}

                {shouldShowAuthor ? (
                  <div className="flex items-start / mb-2">
                    <div className="c-author__container / flex items-start / w-full / mb-1">
                      {author_bar.authorPicture && (
                        <span className="c-author / w-10 h-10 xs:w-12 xs:h-12 / inline-block shrink-0 / bg-gray-300 rounded-full / overflow-hidden / mr-4" />
                      )}
                      <div className="c-author__content w-full">
                        <div
                          className={`flex flex-col sm:flex-row sm:justify-between sm:items-end / w-full ${
                            showReviewedBy ? '' : 'sm:items-end'
                          }`}
                        >
                          <div className="flex flex-col / text-fs13 text-gray-700">
                            <p className="c-author__byline font-bold / sm:mb-1">
                              By {author[0].name}
                              {lastUpdatedDate && !author_bar.dateVariation && (
                                <span className="inline-block w-full sm:w-auto / sm:ml-2 / italic text-gray-600">
                                  Updated on {getCurrentMonth(lastUpdatedDate)}{' '}
                                  {lastUpdatedDate.getDate()},{' '}
                                  {getCurrentYear(lastUpdatedDate)}
                                </span>
                              )}
                            </p>

                            {!author_bar.hideAuthorBio && (
                              <p className="hidden sm:flex">
                                {author[0].description}
                              </p>
                            )}
                            {author_bar.dateVariation && (
                              <p className="inline-block w-full sm:w-auto text-gray-600">
                                Updated on {getCurrentMonth(lastUpdatedDate)}{' '}
                                {lastUpdatedDate.getDate()},{' '}
                                {getCurrentYear(lastUpdatedDate)}
                                {author_bar.timeToRead && (
                                  <span className="inline-block ml-1">
                                    &#8226; {author_bar.timeToRead}
                                  </span>
                                )}
                              </p>
                            )}
                          </div>

                          <div
                            className={`flex flex-col shrink-0 items-start sm:items-end / sm:ml-4 ${
                              showReviewedBy
                                ? 'xs:flex-row sm:flex-col sm:-mt-3'
                                : ''
                            }`}
                          >
                            {showReviewedBy && (
                              <p className="sm:order-2 flex items-center w-full sm:w-auto / mb-1 sm:mb-0 xs:mt-1 / text-fs13 text-gray-600">
                                <span className="inline-block w-4 h-4 / mr-1">
                                  <Image
                                    src="/static/icons/icon-checkmark-secondary.svg"
                                    alt="checkmark"
                                    width={24}
                                    height={24}
                                    aria-hidden={true}
                                    style={nextImageHelper.responsive}
                                    sizes="100vw"
                                  />
                                </span>{' '}
                                Reviewed by{' '}
                                <Link
                                  href={
                                    reviewer?.slug
                                      ? `/author/${reviewer.slug}`
                                      : '#'
                                  }
                                  className="inline-block ml-1 text-primary-mid font-bold / hover:text-primary-bright"
                                >
                                  {reviewer?.name}
                                </Link>
                              </p>
                            )}
                            {socialIconsHide == true ? (
                              ''
                            ) : (
                              <>
                                {author_bar.showSocialIcons && (
                                  <div className="c-social-share / sm:order-1 flex flex-row justify-end items-end / sm:-mt-1">
                                    <SocialShareButtons
                                      headline={headline}
                                      location={location}
                                      seoTitle={headline || undefined}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  lastUpdatedDate && (
                    <>
                      <div>
                        <span className="c-author__date / flex items-start sm:items-center / -mt-2 sm:mb-2 / w-full sm:w-auto / italic / sm:flex-row sm:justify-between / flex-col / text-fs13 text-gray-600 / font-bold">
                          Updated on {getCurrentMonth(lastUpdatedDate)}{' '}
                          {lastUpdatedDate.getDate()},{' '}
                          {getCurrentYear(lastUpdatedDate)}
                        </span>
                      </div>
                      {showUnderlineMobile && (
                        <hr className="c-header__border_exp / border-3 w-24 md:w-32 border-tetriary / mb-5 mt-2" />
                      )}
                    </>
                  )
                )}
              </div>
            </div>
          </header>
        </div>
        <style jsx>{`
          .c-author {
            background-image: url(${author[0].image.url});
            background-size: 125% auto;
            background-position: center top;
          }

          /* ===== Path ===== */
          .c-header__h1-highlight-path {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
            animation: animateHighlightPath 1.25s ease-in forwards;
            animation-delay: 0.75s;
          }
          .c-header__h1-highlight {
            animation: animateTextClr 0.5s ease-in forwards;
            animation-delay: 1.25s;
          }

          /* ===== Animation ===== */
          @keyframes animateHighlightPath {
            to {
              stroke-dashoffset: 0;
            }
          }
          @keyframes animateTextClr {
            to {
              color: #21a3fc;
            }
          }

          /* ===== Path ===== */
          .c-header__h1-highlight-path-1 {
            animation-delay: 0.5s;
          }
          .c-header__h1-highlight-path-2 {
            animation-delay: 1.5s;
          }
          .c-header__h1-highlight-1 {
            animation-delay: 1s;
          }
          .c-header__h1-highlight-2 {
            animation-delay: 2s;
          }
        `}</style>
      </div>
    )
  }
  return TipHeaderRender
}
